import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import type { BulkImportMaterial, IMaterialLotExtended } from "../api";
import { formatVND } from "../utils";
import {
  GlassPanel,
  KpiCard,
  SkeletonTable,
  ErrorState,
  EmptyState,
  Modal,
  toast,
  confirm,
} from "../components";
import type { Column } from "../components";
import { DataTable } from "../components/DataTable";
import { searchItems } from "../search";

/* ── Types ── */
interface Material {
  id: string;
  item_name: string;
  unit_cost: number;
  stock_qty: number;
  fifo_avg_cost: number;
  unit: string;
  base_unit?: string;
  category_name: string | null;
  used_by: { id: string; name: string }[];
  status: "Còn hàng" | "Hết hàng" | "Sắp hết";
}

interface StockLot {
  id: string;
  quantity: number;
  unit_cost: number;
  created_at: string;
}

/** Lot mở rộng từ BE (có expiry/source/reason/supplier_name/consumptions).
 *  id là string từ fetch (StockLot) — BE trả number nên cast khi cần. */
type LotRow = Omit<StockLot & Partial<Omit<IMaterialLotExtended, "id" | "item_id">>, "id"> & {
  id: string;
  idx: number;
};

/* ── Status helpers ── */
const STATUS_DOT: Record<string, "ok" | "warn" | "bad"> = {
  "Còn hàng": "ok",
  "Sắp hết": "warn",
  "Hết hàng": "bad",
};

const STATUS_TEXT: Record<string, string> = {
  "Còn hàng": "Còn hàng",
  "Sắp hết": "Sắp hết",
  "Hết hàng": "Hết hàng",
};

function StatusBadge({ status }: { status: string }) {
  const dot = STATUS_DOT[status] ?? "ok";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px 3px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        background:
          dot === "ok" ? "var(--status-green-bg)"
          : dot === "warn" ? "var(--status-amber-bg)"
          : "var(--status-red-bg)",
        border: `1px solid ${
          dot === "ok" ? "var(--status-green-border)"
          : dot === "warn" ? "var(--status-amber-border)"
          : "var(--status-red-border)"
        }`,
        color:
          dot === "ok" ? "var(--accent-green)"
          : dot === "warn" ? "var(--accent-yellow)"
          : "var(--accent-red)",
      }}
    >
      <span className={`dot ${dot}`} style={{ width: 6, height: 6 }} />
      {STATUS_TEXT[status] ?? status}
    </span>
  );
}

/* ── Page ── */
export default function MaterialsPage() {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lots, setLots] = useState<StockLot[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Ý 1: popup Xuất dùng nguyên liệu
  const [showConsume, setShowConsume] = useState(false);
  const [consumeForm, setConsumeForm] = useState({
    item_id: "",
    quantity: 1,
    reason: "",
    source: "manual" as "manual" | "waste" | "internal" | "return_to_supplier",
  });
  const [consuming, setConsuming] = useState(false);

  const syncSheets = async () => {
    setSyncing(true);
    try {
      await api.syncSheets.toRecipes();
      toast.success("Đã sync từ Google Sheets");
      loadMaterials();
    } catch (e: any) {
      toast.error("Lỗi sync: " + (e?.message ?? "unknown"));
    } finally {
      setSyncing(false);
    }
  };

  /* fetch materials */
  const loadMaterials = () => {
    let alive = true;
    setLoading(true);
    fetch(`${api.baseUrl}/api/stock/materials`, { headers: api.authHeaders() })
      .then((r) => { if (!r.ok) throw r; return r.json(); })
      .then((data: Material[]) => { if (alive) { setMaterials(data); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e?.message ?? "Lỗi tải dữ liệu"); setLoading(false); } });
    return () => { alive = false; };
  };
  useEffect(() => { const cleanup = loadMaterials(); return cleanup; }, []);

  /* fetch lots when row expanded */
  useEffect(() => {
    if (!expandedId) { setLots([]); return; }
    let alive = true;
    setLotsLoading(true);
    fetch(`${api.baseUrl}/api/stock/materials/${expandedId}/lots`, { headers: api.authHeaders() })
      .then((r) => { if (!r.ok) throw r; return r.json(); })
      .then((data: { lots: StockLot[]; consumptions?: any[] }) => { if (alive) { setLots(data.lots || []); setLotsLoading(false); } })
      .catch(() => { if (alive) setLotsLoading(false); });
    return () => { alive = false; };
  }, [expandedId]);

  /* delete material (soft-delete via /api/items/:id) */
  const deleteMaterial = async (m: Material) => {
    const usedWarning = m.used_by.length > 0
      ? `\n\n⚠️ Đang được sử dụng bởi ${m.used_by.length} sản phẩm: ${m.used_by.slice(0, 3).map((u) => u.name).join(", ")}${m.used_by.length > 3 ? "…" : ""}. Xóa sẽ làm hỏng công thức các SP này.`
      : "";
    const ok = await confirm({
      title: "Xóa nguyên liệu",
      danger: true,
      confirmLabel: "Xóa",
      message: `Xóa "${m.item_name}"? Hành động này không thể hoàn tác (soft-delete).${usedWarning}`,
    });
    if (!ok) return;
    try {
      const res = await fetch(`${api.baseUrl}/api/items/${m.id}`, {
        method: "DELETE",
        headers: api.authHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(e.error ?? `HTTP ${res.status}`);
      }
      toast.success(`Đã xóa "${m.item_name}"`);
      setMaterials((prev) => prev.filter((x) => x.id !== m.id));
      setExpandedId(null);
    } catch (e: any) {
      toast.error("Lỗi xóa: " + (e?.message ?? "unknown"));
    }
  };

  /* keyboard: Escape closes drawer */
  useEffect(() => {
    if (!expandedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setExpandedId(null); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [expandedId]);

  /* filter */
  const filtered = searchItems(materials, search, (m) => ({
    id: m.id,
    name: m.item_name,
  })).map(({ item }) => item);

  /* totals for KPIs */
  const totalValue = filtered.reduce((s, m) => s + m.fifo_avg_cost * m.stock_qty, 0);
  const outOfStock = filtered.filter((m) => m.status === "Hết hàng").length;
  const lowStock = filtered.filter((m) => m.status === "Sắp hết").length;

  /* table columns */
  const columns: Column<Material>[] = [
    {
      key: "item_name",
      header: "Tên nguyên liệu",
      render: (row) => (
        <button
          type="button"
          className="btn-ghost"
          style={{
            background: "none",
            border: "none",
            color: "var(--text-primary)",
            fontWeight: 600,
            cursor: "pointer",
            padding: 0,
            textAlign: "left",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
          aria-label={`Xem stock lots của ${row.item_name}`}
        >
          {row.item_name}
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--text-tertiary)" }}>
            chevron_right
          </span>
        </button>
      ),
    },
    {
      key: "fifo_avg_cost",
      header: "Giá FIFO",
      align: "right",
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatVND(row.fifo_avg_cost)}</span>
      ),
    },
    {
      key: "stock_qty",
      header: "Tồn kho",
      align: "right",
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {row.stock_qty.toLocaleString("vi-VN")} <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>{row.unit || "g"}</span>
          {row.base_unit && row.base_unit !== (row.unit || "g") && (
            <span style={{ color: "var(--text-tertiary)", fontSize: 10, marginLeft: 4, opacity: 0.7 }} title={`Đơn vị cơ bản: ${row.base_unit}`}>/ {row.base_unit}</span>
          )}
        </span>
      ),
    },
    {
      key: "_total",
      header: "Giá trị tồn",
      align: "right",
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "var(--text-primary)" }}>
          {formatVND(row.fifo_avg_cost * row.stock_qty)}
        </span>
      ),
    },
    {
      key: "used_by",
      header: "SP sử dụng",
      render: (row) => {
        const arr = row.used_by;
        if (!arr || arr.length === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 260 }}>
            {arr.slice(0, 3).map((u) => (
              <button
                key={u.id}
                onClick={() => { window.location.href = `/products?recipe=${u.id}`; }}
                title={`Mở công thức: ${u.name}`}
                style={{
                  display: "inline-block",
                  padding: "1px 8px",
                  borderRadius: 6,
                  fontSize: 11,
                  background: "var(--accent-purple-bg)",
                  color: "var(--accent-purple)",
                  border: "1px solid rgba(167,139,250,0.18)",
                  cursor: "pointer",
                }}
              >
                {u.name}
              </button>
            ))}
            {arr.length > 3 && (
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>+{arr.length - 3}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "_actions",
      header: "",
      align: "right",
      render: (row) => {
        // Chỉ xoá khi hết tồn + không SP nào dùng (BE soft-delete)
        const canDelete = row.stock_qty <= 0 && (!row.used_by || row.used_by.length === 0);
        if (!canDelete) return null;
        return (
          <button
            type="button"
            className="btn-icon"
            onClick={() => deleteMaterial(row)}
            aria-label={`Xóa nguyên liệu ${row.item_name}`}
            title="Xóa nguyên liệu"
            style={{
              background: "none", border: "none", color: "var(--text-tertiary)",
              cursor: "pointer", padding: 6, display: "inline-flex",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
          </button>
        );
      },
    },
  ];

  /* lots columns — mở rộng cột expiry/source/reason/supplier_name (Ý 5+4) */
  const lotColumns: Column<LotRow>[] = [
    { key: "idx", header: "Lot", render: (r) => <span style={{ color: "var(--text-tertiary)" }}>#{r.idx}</span> },
    {
      key: "quantity", header: "SL", align: "right",
      render: (r) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {r.quantity.toLocaleString("vi-VN")}
        </span>
      ),
    },
    {
      key: "unit_cost", header: "Giá", align: "right",
      render: (r) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatVND(r.unit_cost)}</span>,
    },
    {
      key: "created_at", header: "Ngày nhập",
      render: (r) => (
        <span style={{ color: "var(--text-tertiary)" }}>
          {new Date(r.created_at).toLocaleDateString("vi-VN")}
        </span>
      ),
    },
    {
      key: "supplier_name", header: "NCC",
      render: (r) => (
        <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>
          {r.supplier_name || "—"}
        </span>
      ),
    },
    {
      key: "source", header: "Nguồn",
      render: (r) => (
        <span
          style={{
            fontSize: 11, padding: "2px 6px", borderRadius: 6,
            background: r.source === "import_order" ? "rgba(96,165,250,0.14)" : "rgba(156,163,175,0.14)",
            color: r.source === "import_order" ? "var(--accent-blue)" : "var(--text-tertiary)",
          }}
        >
          {r.source === "import_order" ? "Đơn nhập" : r.source === "manual" ? "Tay" : r.source || "—"}
        </span>
      ),
    },
    {
      key: "expiry_date", header: "Hạn SD",
      render: (r) => {
        const exp = r.expiry_date;
        if (!exp) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
        const expDate = new Date(exp + "T00:00:00");
        if (isNaN(expDate.getTime())) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const days = Math.floor((expDate.getTime() - now.getTime()) / 86400000);
        const over = days < 0;
        const soon = days >= 0 && days < 7;
        const color = over ? "var(--danger, #ef4444)" : soon ? "var(--warning, #f59e0b)" : "var(--text-secondary)";
        const bg = over ? "rgba(239,68,68,0.14)" : soon ? "rgba(245,158,11,0.14)" : "transparent";
        const titleText = over ? "Quá hạn " + Math.abs(days) + " ngày" : soon ? "Còn " + days + " ngày" : "Còn hạn";
        return (
          <span
            title={titleText}
            style={{
              fontSize: 11, padding: "2px 6px", borderRadius: 6, color, background: bg,
              fontWeight: over || soon ? 600 : 400,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {expDate.toLocaleDateString("vi-VN")}
            {over && " · quá"}
            {soon && !over && ` · ${days}d`}
          </span>
        );
      },
    },
    {
      key: "reason", header: "Lý do",
      render: (r) => (
        <span style={{ color: "var(--text-secondary)", fontSize: 12, maxWidth: 160, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.reason || ""}>
          {r.reason || "—"}
        </span>
      ),
    },
  ];

  if (loading) return <GlassPanel><SkeletonTable rows={6} /></GlassPanel>;
  if (error) return <GlassPanel><ErrorState error={error} /></GlassPanel>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" }}>
          Nguyên liệu
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
          {filtered.length} nguyên liệu · giá trị tồn {formatVND(totalValue)}
        </p>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <KpiCard label="Tổng nguyên liệu" value={filtered.length} />
        <KpiCard label="Giá trị tồn" value={formatVND(totalValue)} />
        <KpiCard
          label="Sắp hết"
          value={lowStock}
          note={lowStock > 0 ? "cần nhập thêm" : "đủ hàng"}
          dotColor={lowStock > 0 ? "warn" : "ok"}
        />
        <KpiCard
          label="Hết hàng"
          value={outOfStock}
          note={outOfStock > 0 ? "cần nhập gấp" : "không có"}
          dotColor={outOfStock > 0 ? "bad" : "ok"}
        />
      </div>

      {/* Toolbar + Table */}
      <GlassPanel>
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flex: 1,
              minWidth: 220,
              background: "var(--bg-input)",
              border: "1px solid var(--border-base)",
              borderRadius: 8,
              padding: "0 10px",
              minHeight: 36,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--text-tertiary)" }}>
              search
            </span>
            <input
              type="text"
              placeholder="Tìm nguyên liệu hoặc danh mục..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "var(--text-primary)",
                fontSize: 14,
                padding: "8px 0",
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Xóa tìm kiếm"
                className="btn-icon"
                style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: 2, display: "flex" }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            )}
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => setShowImport(true)}
            style={{ minHeight: 36 }}
            title="Import NL mới từ Excel/Sheets"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            Import NL mới
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setConsumeForm({
                item_id: materials[0]?.id || "",
                quantity: 1,
                reason: "",
                source: "manual",
              });
              setShowConsume(true);
            }}
            disabled={materials.length === 0}
            style={{ minHeight: 36, opacity: materials.length === 0 ? 0.5 : 1 }}
            title="Xuất dùng nguyên liệu (trừ tồn FIFO)"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>output</span>
            Xuất dùng
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={syncSheets}
            disabled={syncing}
            style={{ minHeight: 36, opacity: syncing ? 0.6 : 1 }}
            title="Đồng bộ nguyên liệu từ Google Sheets"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18, animation: syncing ? "spin 1s linear infinite" : undefined }}
            >
              {syncing ? "progress_activity" : "cloud_sync"}
            </span>
            {syncing ? "Đang sync..." : "Sync Sheets"}
          </button>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            state="empty"
            icon="inventory_2"
            title={search ? "Không tìm thấy nguyên liệu" : "Chưa có nguyên liệu nào"}
            description={search ? `Không có NL khớp "${search}"` : "Import nguyên liệu đầu tiên để bắt đầu theo dõi tồn kho."}
            cta={search ? undefined : { label: "+ Import NL mới", onClick: () => setShowImport(true) }}
          />
        ) : (
          <DataTable
            rows={filtered}
            rowKey={(r) => r.id}
            columns={columns}
          />
        )}
      </GlassPanel>

      {/* Lots POPUP (centered, mirror ProductsPage recipe popup — Ý 8) */}
      {/* createPortal: render ra document.body để tránh ancestor .page-wrap.fade-up (transform) thành containing block → position:fixed bị lệch viewport */}
      {expandedId && (() => {
        const mat = materials.find((m) => m.id === expandedId);
        if (!mat) return null;
        return createPortal(
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 999,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)",
            }}
            onClick={() => setExpandedId(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Stock lots ${mat.item_name}`}
              style={{
                width: "min(640px, 100%)", maxWidth: 480, maxHeight: "92vh",
                background: "var(--bg-surface)", border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 14, boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
                display: "flex", flexDirection: "column", overflow: "hidden",
                animation: "popIn 0.2s ease-out",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.10)",
                  display: "flex", alignItems: "center", gap: 10, background: "var(--bg-card)",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--accent-blue)" }}>inventory_2</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Stock lots
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {mat.item_name} · tồn {mat.stock_qty.toLocaleString("vi-VN")} · {formatVND(mat.fifo_avg_cost)}/u
                  </div>
                </div>
                <button className="btn btn-ghost" onClick={() => setExpandedId(null)} style={{ padding: 4, minWidth: "auto" }} title="Đóng" aria-label="Đóng">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 20px" }}>
                {lotsLoading ? (
                  <SkeletonTable rows={3} />
                ) : lots.length === 0 ? (
                  <EmptyState
                    state="empty"
                    icon="inventory"
                    title="Không có stock lot"
                    description="Chưa có lô nhập kho nào cho nguyên liệu này."
                  />
                ) : (
                  <>
                    <DataTable
                      rows={lots.map((l, i) => ({ ...l, expiry_date: (l as any).expiry_date, source: (l as any).source, reason: (l as any).reason, supplier_name: (l as any).supplier_name, idx: i + 1 }))}
                      rowKey={(r) => r.id}
                      columns={lotColumns}
                    />
                    {/* totals */}
                    <div
                      style={{
                        marginTop: 12, padding: "12px 16px",
                        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
                        borderRadius: 8,
                        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12,
                      }}
                    >
                      <div>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tổng SL</p>
                        <p style={{ margin: "2px 0 0", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
                          {lots.reduce((s, l) => s + l.quantity, 0).toLocaleString("vi-VN")}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Giá FIFO TB</p>
                        <p style={{ margin: "2px 0 0", fontSize: 16, fontWeight: 700, color: "var(--accent-blue)", fontVariantNumeric: "tabular-nums" }}>
                          {formatVND(mat.fifo_avg_cost)}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tổng giá trị</p>
                        <p style={{ margin: "2px 0 0", fontSize: 16, fontWeight: 700, color: "var(--accent-green)", fontVariantNumeric: "tabular-nums" }}>
                          {formatVND(lots.reduce((s, l) => s + l.unit_cost * l.quantity, 0))}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {showImport && (
        <BulkImportModal
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); loadMaterials(); }}
        />
      )}

      {/* ── Ý 1: Xuất dùng nguyên liệu popup ── */}
      {showConsume && (
        <ConsumeMaterialModal
          materials={materials.map((m) => ({ id: m.id, name: m.item_name, stock: m.stock_qty, unit: m.unit }))}
          form={consumeForm}
          setForm={setConsumeForm}
          consuming={consuming}
          onClose={() => setShowConsume(false)}
          onSubmit={async () => {
            if (!consumeForm.item_id) { toast.error("Chọn nguyên liệu"); return; }
            if (consumeForm.quantity <= 0) { toast.error("Số lượng phải > 0"); return; }
            if (!consumeForm.reason.trim()) { toast.error("Nhập lý do xuất dùng"); return; }
            const mat = materials.find((m) => m.id === consumeForm.item_id);
            const matLabel = mat ? `${mat.item_name} (tồn ${mat.stock_qty.toLocaleString("vi-VN")} ${mat.unit})` : "nguyên liệu";
            const ok = await confirm({
              title: "Xuất dùng nguyên liệu?",
              message: `Trừ ${consumeForm.quantity.toLocaleString("vi-VN")} ${mat?.unit || ""} khỏi ${matLabel}.\nLý do: ${consumeForm.reason.trim()}\nNguồn: ${consumeForm.source}`,
            });
            if (!ok) return;
            setConsuming(true);
            try {
              const res = await api.materials.consumeMaterial(
                consumeForm.item_id,
                consumeForm.quantity,
                consumeForm.reason.trim(),
                consumeForm.source,
              );
              toast.success(
                `Đã xuất ${res.consumed.toLocaleString("vi-VN")} ${mat?.unit || ""}` +
                ` · còn ${res.remaining_stock.toLocaleString("vi-VN")} ${mat?.unit || ""}`,
              );
              setShowConsume(false);
              loadMaterials();
            } catch (e: any) {
              toast.error(e?.message || "Xuất dùng thất bại");
            } finally {
              setConsuming(false);
            }
          }}
        />
      )}
    </div>
  );
}

/* ── W10: Bulk import modal ─────────────────────────────────────── */
interface ImportRow {
  name: string;
  cost: string;
  stock_qty: string;
  unit: string;
  base_unit: string;
  supplier_id: string;
  note: string;
  import_date: string;
  expiry_date: string;
}

/** Infer đơn vị cơ bản (g/ml/pcs) từ unit nhập vào. */
function inferBase(unit: string): string {
  const u = unit.toLowerCase().trim();
  if (["g", "kg"].includes(u)) return "g";
  if (["ml", "l"].includes(u)) return "ml";
  return "pcs";
}

const EMPTY_ROW: ImportRow = {
  name: "", cost: "", stock_qty: "", unit: "g",
  base_unit: "", supplier_id: "", note: "", import_date: "", expiry_date: "",
};

function BulkImportModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [rows, setRows] = useState<ImportRow[]>([{ ...EMPTY_ROW }]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ count: number; total: number; errors: { name: string; error: string }[] } | null>(null);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);

  /* fetch suppliers khi modal mount — GET /api/suppliers (type=purchase) */
  useEffect(() => {
    const ac = new AbortController();
    fetch(`${api.baseUrl}/api/suppliers?type=purchase`, { headers: api.authHeaders(), signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { suppliers?: { id: string; name: string }[] }) => {
        if (Array.isArray(data?.suppliers)) setSuppliers(data.suppliers);
      })
      .catch(() => { /* bỏ qua — fallback input text trống */ });
    return () => ac.abort();
  }, []);

  const updateRow = (i: number, field: keyof ImportRow, v: string) => {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [field]: v };
      /* khi đổi unit → auto-infer base_unit nếu đang trống */
      if (field === "unit" && !next.base_unit) next.base_unit = inferBase(v);
      return next;
    }));
  };

  /* paste từ Excel/Sheets: TSV hoặc CSV — 4-7 cột:
     Tên · Đơn giá · SL · Đơn vị [· Đơn vị cơ bản · NCC · Ghi chú] */
  const handlePaste = (e: React.ClipboardEvent, atRow: number) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes(",") && !text.includes("\n")) return;
    e.preventDefault();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const parsed: ImportRow[] = lines.map((line) => {
      const parts = line.includes("\t") ? line.split("\t") : line.split(",");
      const unit = (parts[3] || "g").trim() || "g";
      const base = (parts[4] || "").trim() || inferBase(unit);
      return {
        name: (parts[0] || "").trim(),
        cost: (parts[1] || "").trim(),
        stock_qty: (parts[2] || "").trim(),
        unit,
        base_unit: base,
        supplier_id: (parts[5] || "").trim(),
        note: (parts[6] || "").trim(),
        import_date: "",
        expiry_date: "",
      };
    });
    setRows((prev) => {
      const next = [...prev];
      next.splice(atRow, 1, ...parsed);
      return next;
    });
  };

  const submit = async () => {
    const items: BulkImportMaterial[] = rows
      .filter((r) => {
        if (!r.name.trim()) return false;
        if (isNaN(Number(r.cost)) || Number(r.cost) < 0) return false;
        if (isNaN(Number(r.stock_qty)) || Number(r.stock_qty) < 0) return false;
        return true;
      })
      .map((r) => ({
        name: r.name.trim(),
        cost: Number(r.cost) || 0,
        stock_qty: Number(r.stock_qty) || 0,
        unit: r.unit.trim() || "g",
        base_unit: r.base_unit.trim() || undefined,
        supplier_id: r.supplier_id.trim() || undefined,
        note: r.note.trim() || undefined,
        import_date: r.import_date.trim() || undefined,
        expiry_date: r.expiry_date.trim() || undefined,
      }));
    const skipped = rows.length - items.length;
    if (skipped > 0) toast.warning(`Bỏ qua ${skipped} dòng (thiếu tên/giá âm)`);
    if (items.length === 0) { toast.error("Không có dòng hợp lệ"); return; }
    setSubmitting(true);
    try {
      const res = await api.materials.bulkImport(items);
      setResult({ count: res.count, total: items.length, errors: res.errors });
      if (res.count > 0) setTimeout(onDone, 1200);
    } catch (e) {
      setResult({ count: 0, total: items.length, errors: [{ name: "(fetch)", error: (e as Error).message }] });
    } finally {
      setSubmitting(false);
    }
  };

  const validCount = rows.filter((r) => r.name.trim()).length;

  return (
    <Modal
      open
      onClose={onClose}
      title="Import nguyên liệu mới"
      width={880}
      footer={
        <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setRows((p) => [...p, { ...EMPTY_ROW }])}
            style={{ minHeight: 36 }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            Thêm dòng
          </button>
          <div style={{ flex: 1 }} />
          {result && (
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              Đã nhập <b style={{ color: "var(--accent-green)" }}>{result.count}</b>/{result.total}
              {result.errors.length > 0 && (
                <span style={{ color: "var(--accent-red)" }}> · {result.errors.length} lỗi</span>
              )}
            </span>
          )}
          <button
            type="button"
            className="btn"
            onClick={submit}
            disabled={submitting}
            style={{ minHeight: 36, opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? (
              <>
                <span className="material-symbols-outlined spin" style={{ fontSize: 18, animation: "spin 1s linear infinite" }}>progress_activity</span>
                Đang nhập...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>upload</span>
                Nhập {validCount} NL
              </>
            )}
          </button>
        </div>
      }
    >
      <p style={{ color: "var(--text-secondary)", fontSize: 12, margin: "0 0 12px" }}>
        Nhập tay hoặc dán 4-7 cột: <b>Tên</b> · <b>Đơn giá</b> · <b>SL</b> · <b>Đơn vị</b> [· <b>Đơn vị cơ bản</b> · <b>NCC</b> · <b>Ghi chú</b>]. Đơn vị cơ bản để trống → auto (g/ml/pcs).
      </p>

      {/* row cards */}
      <datalist id="material-units">
        {["g", "kg", "ml", "l", "pcs", "chục", "hộp"].map((u) => (<option key={u} value={u} />))}
      </datalist>
      <datalist id="material-base-units">
        {["g", "ml", "pcs"].map((u) => (<option key={u} value={u} />))}
      </datalist>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r, i) => {
          const err = result?.errors.find((e) => e.name === r.name.trim());
          const invalid = err || (r.name.trim() && (isNaN(Number(r.cost)) || isNaN(Number(r.stock_qty))));
          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 1fr 1fr 0.7fr 0.7fr auto",
                gap: 8,
                alignItems: "center",
                padding: "10px 12px",
                background: invalid ? "rgba(248,113,113,0.05)" : "var(--bg-card)",
                border: `1px solid ${invalid ? "var(--status-red-border)" : "var(--border-subtle)"}`,
                borderRadius: 8,
              }}
            >
              <input
                type="text"
                value={r.name}
                onChange={(e) => updateRow(i, "name", e.target.value)}
                onPaste={(e) => handlePaste(e, i)}
                placeholder={`Nguyên liệu ${i + 1}`}
                style={{
                  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                  borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                  fontSize: 13, minHeight: 36,
                }}
              />
              <input
                type="number"
                value={r.cost}
                onChange={(e) => updateRow(i, "cost", e.target.value)}
                onPaste={(e) => handlePaste(e, i)}
                placeholder="Đơn giá"
                style={{
                  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                  borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                  fontSize: 13, fontVariantNumeric: "tabular-nums", minHeight: 36,
                }}
              />
              <input
                type="number"
                value={r.stock_qty}
                onChange={(e) => updateRow(i, "stock_qty", e.target.value)}
                onPaste={(e) => handlePaste(e, i)}
                placeholder="Số lượng"
                style={{
                  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                  borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                  fontSize: 13, fontVariantNumeric: "tabular-nums", minHeight: 36,
                }}
              />
              <input
                type="text"
                value={r.unit}
                onChange={(e) => updateRow(i, "unit", e.target.value)}
                onPaste={(e) => handlePaste(e, i)}
                placeholder="Đơn vị"
                list="material-units"
                style={{
                  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                  borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                  fontSize: 13, minHeight: 36,
                }}
              />
              <input
                type="text"
                value={r.base_unit}
                onChange={(e) => updateRow(i, "base_unit", e.target.value)}
                onPaste={(e) => handlePaste(e, i)}
                placeholder="Cơ bản"
                list="material-base-units"
                title="Đơn vị cơ bản (g/ml/pcs) — để trống tự suy"
                style={{
                  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                  borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                  fontSize: 13, minHeight: 36,
                }}
              />
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                  aria-label="Xóa dòng"
                  className="btn-icon"
                  style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: 4, display: "flex", flexShrink: 0, alignSelf: "flex-start" }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                </button>
              )}
              {/* Hàng 2: NCC · Ngày nhập · Hạn SD · Ghi chú */}
              <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.8fr", gap: 8, alignItems: "center" }}>
                {suppliers.length > 0 ? (
                  <select
                    value={r.supplier_id}
                    onChange={(e) => updateRow(i, "supplier_id", e.target.value)}
                    style={{
                      width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                      borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                      fontSize: 13, minHeight: 36,
                    }}
                  >
                    <option value="">— NCC —</option>
                    {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={r.supplier_id}
                    onChange={(e) => updateRow(i, "supplier_id", e.target.value)}
                    placeholder="NCC (id/tên)"
                    style={{
                      width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                      borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                      fontSize: 13, minHeight: 36,
                    }}
                  />
                )}
                <input
                  type="date"
                  value={r.import_date}
                  onChange={(e) => updateRow(i, "import_date", e.target.value)}
                  title="Ngày nhập"
                  style={{
                    width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                    borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                    fontSize: 13, minHeight: 36,
                  }}
                />
                <input
                  type="date"
                  value={r.expiry_date}
                  onChange={(e) => updateRow(i, "expiry_date", e.target.value)}
                  title="Hạn sử dụng (tuỳ chọn)"
                  style={{
                    width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                    borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                    fontSize: 13, minHeight: 36,
                  }}
                />
                <input
                  type="text"
                  value={r.note}
                  onChange={(e) => updateRow(i, "note", e.target.value)}
                  placeholder="Ghi chú..."
                  style={{
                    width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                    borderRadius: 6, outline: "none", color: "var(--text-primary)", padding: "8px 10px",
                    fontSize: 13, minHeight: 36,
                  }}
                />
              </div>
              {err && (
                <div style={{ gridColumn: "1 / -1", color: "var(--accent-red)", fontSize: 11, marginTop: -2 }}>
                  {err.error}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {result && result.errors.length > 0 && (
        <div
          style={{
            marginTop: 12, padding: 10, borderRadius: 8,
            background: "var(--status-red-bg)", border: "1px solid var(--status-red-border)",
            fontSize: 13, color: "var(--accent-red)",
          }}
        >
          <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Chi tiết lỗi:</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {result.errors.map((e, i) => (<li key={i}>{e.name}: {e.error}</li>))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

/* ── Ý 1: Xuất dùng nguyên liệu popup ── */
const CONSUME_SOURCES: { value: "manual" | "waste" | "internal" | "return_to_supplier"; label: string }[] = [
  { value: "manual", label: "Sản xuất / dùng tay" },
  { value: "waste", label: "Hao hụt / hư hỏng" },
  { value: "internal", label: "Chuyển nội bộ" },
  { value: "return_to_supplier", label: "Trả lại NCC" },
];

interface ConsumeMaterialModalProps {
  materials: { id: string; name: string; stock: number; unit: string }[];
  form: { item_id: string; quantity: number; reason: string; source: "manual" | "waste" | "internal" | "return_to_supplier" };
  setForm: React.Dispatch<React.SetStateAction<{ item_id: string; quantity: number; reason: string; source: "manual" | "waste" | "internal" | "return_to_supplier" }>>;
  consuming: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

function ConsumeMaterialModal({ materials, form, setForm, consuming, onClose, onSubmit }: ConsumeMaterialModalProps) {
  const selected = materials.find((m) => m.id === form.item_id);
  const sourceLabel = (CONSUME_SOURCES.find((s) => s.value === form.source)?.label || form.source) as unknown as string;
  return (
    <Modal
      open
      onClose={onClose}
      title="Xuất dùng nguyên liệu"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={consuming}>Huỷ</button>
          <button className="btn btn-danger" onClick={onSubmit} disabled={consuming}>
            {consuming ? "Đang xử lý..." : "Xuất dùng"}
          </button>
        </>
      }
    >
      <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-secondary)" }}>
        Trừ tồn kho theo FIFO. Mỗi lot được trừ theo thứ tự nhập trước xuất trước.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Nguyên liệu *</span>
          <select
            value={form.item_id}
            onChange={(e) => setForm((p) => ({ ...p, item_id: e.target.value }))}
            style={{
              width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
              borderRadius: 6, padding: "9px 10px", outline: "none", color: "var(--text-primary)", fontSize: 14, minHeight: 38,
            }}
          >
            <option value="">— Chọn nguyên liệu —</option>
            {materials.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · tồn {m.stock.toLocaleString("vi-VN")} {m.unit}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Số lượng *</span>
            <input
              type="number"
              min={0}
              step="any"
              value={form.quantity}
              onChange={(e) => setForm((p) => ({ ...p, quantity: Math.max(0, Number(e.target.value)) }))}
              style={{
                width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                borderRadius: 6, padding: "9px 10px", outline: "none", color: "var(--text-primary)", fontSize: 14, minHeight: 38,
              }}
            />
            {selected && (
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                Tồn hiện tại: {selected.stock.toLocaleString("vi-VN")} {selected.unit}
              </span>
            )}
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Nguồn xuất</span>
            <select
              value={form.source}
              onChange={(e) => setForm((p) => ({ ...p, source: e.target.value as typeof form.source }))}
              title={sourceLabel}
              style={{
                width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
                borderRadius: 6, padding: "9px 10px", outline: "none", color: "var(--text-primary)", fontSize: 14, minHeight: 38,
              }}
            >
              {CONSUME_SOURCES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Lý do *</span>
          <textarea
            value={form.reason}
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
            placeholder="VD: Sản xuất mẻ sáp 1001, hao hụt đổ vỡ..."
            rows={3}
            style={{
              width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-base)",
              borderRadius: 6, padding: "9px 10px", outline: "none", color: "var(--text-primary)", fontSize: 14,
              resize: "vertical", fontFamily: "inherit", minHeight: 70,
            }}
          />
        </label>
      </div>
    </Modal>
  );
}
